import { defineStore } from 'pinia'
import { encryptedStorage } from '@/utils/encryption'
import axios from '@/plugins/axiosInstance'
import { socialMedia } from '@/networking/urlManager'

export const useSocialPostsStore = defineStore('socialPosts', {
  state: () => ({
    selectedPostId: null,
    postsList: [],
    postDetails: {}, // Post detayları için önbellek
    postDetailLastFetched: {}, // Son çekilme zamanları
    postsListLastFetched: 0, // Post listesinin son çekilme zamanı 
    currentPage: 1,
    totalCount: 0,
    pageCount: 1,
    itemsPerPage: 15,
    sortKey: '',
    sortOrder: 'DESC',
    searchQuery: '',
    selectedFilter: null,
    lastActiveView: 'list', // 'list' veya 'detail' - şu anda hangi görünümdeyiz
    lastPageParams: {}, // Son sayfa parametreleri (filtreler, sıralama vb.)
    lastRequestParams: null, // Son API isteğinin parametreleri
    requestCounter: 0, // Kaç kez istek gönderildiğini takip etmek için
  }),

  actions: {
    // Son istek parametrelerini kontrol et - aynı istek mi?
    isSameRequest(params = {}) {
      try {
        // İki parametre string'inin aynı olup olmadığını kontrol eder
        // Bu metod, request uri'yi tamamen kopyalayarak karşılaştırır
        const buildRequestString = (p) => {
          const base = `/social-posts?assetId=${p.assetId || ''}`; 
          const query = `&q=${p.q || ''}`;
          const page = `&page=${p.page !== undefined ? p.page : 0}`;
          const sort = `&sort=${p.sort || ''}`;
          const order = `&order=${p.order || 'DESC'}`;
          let filter = '';
          if (p.filter) filter = `&filter=${p.filter}`;
          
          return `${base}${query}${page}${sort}${order}${filter}`;
        };
        
        // Mevcut istek parametre string'ini oluştur
        const currentParams = {
          assetId: params.assetId || this.lastRequestParams?.assetId,
          q: params.q || this.searchQuery || '',
          page: params.page !== undefined ? params.page : this.currentPage - 1,
          sort: params.sort || this.sortKey || '',
          order: params.order || this.sortOrder || 'DESC',
          filter: params.filter || this.selectedFilter || null
        };
        
        // forceRefresh parametresini dikkate alma
        if ('forceRefresh' in currentParams) {
          delete currentParams.forceRefresh;
        }
        
        // Eğer daha önce bir istek yapılmamışsa
        if (!this.lastRequestParams) {
          return false;
        }
        
        // İki istek için tam URI string'leri oluştur
        const currentRequestString = buildRequestString(currentParams);
        const lastRequestString = buildRequestString(this.lastRequestParams);
        
        // Tam request string'lerini karşılaştır
        return currentRequestString === lastRequestString;
      } catch (err) {
        console.error('isSameRequest hata:', err);
        return false; // Hata durumunda false döndür
      }
    },
    
    setSelectedPostId(postId) {
      this.selectedPostId = postId
    },
    
    clearSelectedPost() {
      this.selectedPostId = null
    },
    
    setPostsList(posts) {
      this.postsList = posts
      this.postsListLastFetched = Date.now()
      
      // Liste görünümünde olduğumuzu işaretle
      this.lastActiveView = 'list'
      
      // Mevcut sayfa parametrelerini kaydet
      this.lastPageParams = {
        page: this.currentPage,
        sortKey: this.sortKey,
        sortOrder: this.sortOrder,
        searchQuery: this.searchQuery,
        selectedFilter: this.selectedFilter
      }
    },
    
    // Post detayını önbelleğe kaydet
    setPostDetail(postId, postData) {
      this.postDetails[postId] = postData
      this.postDetailLastFetched[postId] = Date.now()
      
      // Detay görünümünde olduğumuzu işaretle
      this.lastActiveView = 'detail'
    },
    
    // Post detayını önbellekten al
    getPostDetail(postId) {
      return this.postDetails[postId] || null
    },
    
    // Son çekilme zamanı kontrolü - 2 dakika içinde çekilmiş mi
    isPostDetailFresh(postId) {
      const lastFetched = this.postDetailLastFetched[postId] || 0
      const now = Date.now()
      const twoMinutes = 2 * 60 * 1000 // 2 dakika (milisaniye cinsinden)
      
      return lastFetched > 0 && (now - lastFetched) < twoMinutes
    },

    // Post listesi için önbellekleme kontrolü - 2 dakika
    isPostsListFresh(forceRefresh = false) {
      try {
        // Eğer forcefully refresh isteniyorsa, her zaman false döndür
        if (forceRefresh) {
          return false;
        }
        
        // Önbellek süresi zaten sıfırsa (setSearchParams tarafından sıfırlanmış olabilir)
        if (this.postsListLastFetched === 0) {
          return false;
        }
        
        // Parametreler değişti mi kontrol et
        if (typeof this.isSameRequest === 'function') {
          const isSame = this.isSameRequest();
          if (!isSame) {
            return false;
          }
        }
        
        // Eğer view değişmişse (detaydan listeye dönüş) ve önbellek 5 saniyeden eski ise refresh et
        if (this.lastActiveView !== 'list') {
          const lastFetched = this.postsListLastFetched || 0;
          const now = Date.now();
          const fiveSeconds = 5 * 1000; // 5 saniye
          
          if ((now - lastFetched) > fiveSeconds) {
            return false; // Önbellek taze değil, yeniden yükle
          }
        }
        
        // Normal taze kontrolü - aynı sayfada kalındığında 2 dakika önbellek süresi kullan
        const lastFetched = this.postsListLastFetched || 0;
        const now = Date.now();
        const twoMinutes = 2 * 60 * 1000; // 2 dakika - aynı sayfada daha uzun önbellekleme

        const isFresh = lastFetched > 0 && (now - lastFetched) < twoMinutes;
        return isFresh;
      } catch (err) {
        console.error('isPostsListFresh hata:', err);
        return false; // Hata durumunda false döndür, yeni istek yap
      }
    },

    // Post listesini API'den çek
    async fetchPostsList(assetId, params = {}) {
      try {
        // Görünüm değişikliğini kontrol et
        const isViewChange = this.lastActiveView !== 'list';
        
        // Önbellek anahtarını oluştur - assetId dahil
        const defaultParams = {
          assetId: assetId,
          q: this.searchQuery,
          page: this.currentPage - 1,
          sort: this.sortKey,
          order: this.sortOrder,
          filter: this.selectedFilter
        };

        // Tüm parametreleri birleştir
        const queryParams = { ...defaultParams, ...params };
        
        // forceRefresh özellikle çıkar
        const forceRefresh = queryParams.forceRefresh;
        delete queryParams.forceRefresh;
        
        // Aynı parametreler için istek var mı kontrol et (assetId dahil)
        const isSameRequest = this.isSameRequest(queryParams);
        
        // Önbellekte yeni veri varsa ve yeterince taze ise
        if (isSameRequest && this.isPostsListFresh(forceRefresh) && !isViewChange) {
          return {
            detail: this.postsList,
            totalCount: this.totalCount,
            pageCount: this.pageCount
          };
        }

        // İstek sayacını artır
        this.requestCounter++;

        // İstek parametrelerini sakla - sonraki isteklerle karşılaştırmak için
        // Önemli: istekten önce güncelle ki aynı istek iki kez gelmesin
        this.lastRequestParams = {...queryParams};

        // urlManager'dan gelen endpoint'i kullan
        const { data } = await axios.get(socialMedia.getPosts, {
          params: queryParams,
        })

        // Verileri state'e ve önbelleğe kaydet
        this.postsList = data.data.detail
        this.totalCount = data.data.totalCount
        this.pageCount = data.data.pageCount
        this.postsListLastFetched = Date.now()
        
        return {
          detail: data.data.detail,
          totalCount: data.data.totalCount,
          pageCount: data.data.pageCount
        }
      } catch (error) {
        console.error('Error fetching posts list:', error)
        return {
          detail: [],
          totalCount: 0,
          pageCount: 0
        }
      }
    },

    // Post detayını API'den çek
    async fetchPostDetail(postId, assetId) {
      // Önbellekte yeni veri varsa ve yeterince taze ise
      if (this.isPostDetailFresh(postId)) {
        return this.postDetails[postId]
      }

      try {
        // urlManager'dan gelen endpoint'i kullan
        const response = await axios.get(socialMedia.getPost, {
          params: {
            postId: postId,
            assetId,
          },
        })

        if (response?.data?.data) {
          // Önbellekte sakla
          this.setPostDetail(postId, response.data.data)
          return response.data.data
        }
        return null
      } catch (error) {
        console.error('Error fetching post detail:', error)
        return null
      }
    },
    
    setPageInfo(pageInfo) {
      this.currentPage = pageInfo.currentPage
      this.totalCount = pageInfo.totalCount
      this.pageCount = pageInfo.pageCount
    },
    
    setSearchParams(params) {
      let paramChanged = false;
      
      // Her bir parametre için değişiklik kontrolü
      if (params.page !== undefined && this.currentPage !== params.page) {
        this.currentPage = params.page;
        paramChanged = true;
      }
      
      if (params.sortKey !== undefined && this.sortKey !== params.sortKey) {
        this.sortKey = params.sortKey;
        paramChanged = true;
      }
      
      if (params.sortOrder !== undefined && this.sortOrder !== params.sortOrder) {
        this.sortOrder = params.sortOrder;
        paramChanged = true;
      }
      
      if (params.searchQuery !== undefined && this.searchQuery !== params.searchQuery) {
        this.searchQuery = params.searchQuery;
        paramChanged = true;
      }
      
      if (params.selectedFilter !== undefined && this.selectedFilter !== params.selectedFilter) {
        this.selectedFilter = params.selectedFilter;
        paramChanged = true;
      }
      
      // Parametre değişiminde önbelleği geçersiz kıl
      if (paramChanged) {
        this.postsListLastFetched = 0; // Önbellekleme süresini sıfırla
        this.lastRequestParams = null; // Son istek parametrelerini sıfırla
      }
    },
    
    // Yönlendirme yaparken post ID'sini store'a kaydet
    showPostDetail(router, postId) {
      // Post ID'sini store'a kaydet
      this.setSelectedPostId(postId)
      
      // Görünüm değişiyor, detay moduna geçiliyor
      this.lastActiveView = 'detail'
      
      // view=detail parametresini ekle, postId'yi eklemiyoruz
      router.push({ 
        query: { 
          ...router.currentRoute.value.query, 
          view: 'detail' 
        }
      })
    },
    
    // Listeye dönerken parametreleri temizle
    hidePostDetail(router) {
      // Önce store'daki post ID'sini temizle
      this.selectedPostId = null;
      
      // Görünüm değişiyor, liste moduna geçiliyor
      this.lastActiveView = 'list';
      
      // Listeye dönerken, sonraki yüklemede post listesini yenile
      // Yani önbelleği zorla yenile - bir sonraki fetch işleminde kullanmak üzere
      // önbellekleme süresini sıfırla
      this.postsListLastFetched = Date.now() - 31 * 1000; // 30 saniyeden daha eski
      
      // Sonra sadece view parametresini kaldır
      const routeQuery = router.currentRoute.value.query;
      const { view, ...otherQuery } = routeQuery;
      
      // Route'u güncelleyerek view parametresini kaldır
      router.push({ query: otherQuery });
    }
  },

  getters: {
    getSelectedPostId: (state) => state.selectedPostId,
    getSelectedPost: (state) => {
      // Öncelikle postDetails'de kontrol et, yoksa postsList'te bul
      const fromCache = state.postDetails[state.selectedPostId] || null
      if (fromCache) return fromCache
      
      // Eğer önbellekte yoksa listeden bul
      return state.postsList.find(post => post.id === state.selectedPostId) || null
    }
  },

  persist: {
    key: 'social-posts-store',
    storage: encryptedStorage,
    paths: ['selectedPostId', 'postDetails', 'postDetailLastFetched', 'postsListLastFetched', 'postsList', 'currentPage', 'sortKey', 'sortOrder', 'searchQuery', 'selectedFilter', 'lastActiveView', 'lastPageParams', 'lastRequestParams', 'requestCounter'],
  },
})